import { useState, useEffect } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import { supabase } from '@/integrations/supabase/client'
import { useAuth } from '@/hooks/useAuth'
import SEO from '@/components/university/common/SEO'
import UniversityHeader from '@/components/university/layout/UniversityHeader';
import UniversityFooter from '@/components/university/layout/UniversityFooter';

const M = '#06060c'
const G = '#d4a843'
const IN = '#0ea5e9'
const BR = 'rgba(255,255,255,0.07)'
const CA = 'rgba(255,255,255,0.04)'
const TX = 'rgba(255,255,255,0.88)'
const MU = 'rgba(255,255,255,0.45)'

export default function RedeemPage() {
  const navigate = useNavigate()
  const { code: urlCode } = useParams<{ code?: string }>()
  const [searchParams] = useSearchParams()
  const { t } = useTranslation()
  const { user, isLoading: authLoading } = useAuth()

  const [code, setCode] = useState(urlCode || searchParams.get('code') || '')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<{
    donor_name: string
    personal_message: string
    plan_key: string
    expires_at: string | null
  } | null>(null)

  // Auto-redeem if code is in URL and user is logged in
  useEffect(() => {
    if (urlCode && user && !authLoading) {
      handleRedeem(urlCode)
    }
  }, [urlCode, user, authLoading])

  async function handleRedeem(redeemCode?: string) {
    const c = (redeemCode || code).trim().toUpperCase()
    if (!c) { setError(t('university.redeem.errorNoCode', 'Please enter your gift code.')); return }

    if (!user) {
      // Save code to sessionStorage and redirect to sign up
      sessionStorage.setItem('pending_gift_code', c)
      navigate(`/signup?redirect=/redeem/${c}`)
      return
    }

    setLoading(true); setError(null)

    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session?.access_token) throw new Error('Not authenticated')

      const supabaseUrl = (supabase as unknown as { supabaseUrl: string }).supabaseUrl

      const res = await fetch(`${supabaseUrl}/functions/v1/redeem-gift`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ gift_code: c }),
      })

      const data = await res.json()

      if (!res.ok) throw new Error(data.error || 'Redemption failed')

      setSuccess({
        donor_name: data.donor_name || 'Someone special',
        personal_message: data.personal_message || '',
        plan_key: data.plan_key,
        expires_at: data.expires_at,
      })
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setLoading(false)
    }
  }

  function getPlanLabel(key: string) {
    if (key === 'university_single') return t('university.redeem.planSingle', 'Single Degree — Permanent Access')
    if (key === 'university_monthly') return t('university.redeem.planMonthly', 'Full Access — 1 Month')
    if (key === 'university_annual') return t('university.redeem.planAnnual', 'Full Access — 1 Year')
    return key
  }

  if (success) {
    return (
      <>
      <UniversityHeader/>
      <div style={{ background: M, minHeight: '100vh', color: TX, fontFamily: "'Inter', system-ui, sans-serif", display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <SEO title="Gift Claimed" noindex />
        <motion.div initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.4 }}
          style={{ maxWidth: 520, width: '100%', margin: '0 auto', padding: '40px 24px', textAlign: 'center' }}>

          <motion.div animate={{ rotate: [0, -10, 10, -5, 5, 0] }} transition={{ delay: 0.3, duration: 0.6 }}
            style={{ fontSize: 72, marginBottom: 24 }}>🎓</motion.div>

          <h1 style={{ fontSize: 32, fontWeight: 900, margin: '0 0 12px', fontFamily: "'Playfair Display', Georgia, serif", letterSpacing: '-0.5px' }}>
            {t('university.redeem.successTitle', 'Your Education Starts Now')}
          </h1>

          <p style={{ fontSize: 15, color: MU, lineHeight: 1.7, margin: '0 0 24px' }}>
            {t('university.redeem.successFrom', 'A gift from')} <strong style={{ color: TX }}>{success.donor_name}</strong>
          </p>

          {success.personal_message && (
            <div style={{ background: 'rgba(212,168,67,0.06)', border: '1.5px solid rgba(212,168,67,0.25)', borderRadius: 16, padding: '20px 24px', marginBottom: 24, textAlign: 'left' }}>
              <p style={{ fontSize: 11, color: G, fontWeight: 800, letterSpacing: '1.5px', marginBottom: 10 }}>
                {t('university.redeem.message', 'THEIR MESSAGE')}
              </p>
              <p style={{ fontSize: 15, color: TX, lineHeight: 1.65, fontStyle: 'italic', margin: 0 }}>
                "{success.personal_message}"
              </p>
            </div>
          )}

          <div style={{ background: CA, border: `1px solid ${BR}`, borderRadius: 14, padding: '16px 20px', marginBottom: 32, textAlign: 'left' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <span style={{ fontSize: 13, color: MU }}>{t('university.redeem.plan', 'Plan')}</span>
              <span style={{ fontSize: 13, fontWeight: 700, color: G }}>{getPlanLabel(success.plan_key)}</span>
            </div>
            {success.expires_at && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 13, color: MU }}>{t('university.redeem.accessUntil', 'Access until')}</span>
                <span style={{ fontSize: 13, fontWeight: 700, color: TX }}>{new Date(success.expires_at).toLocaleDateString()}</span>
              </div>
            )}
            {!success.expires_at && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 13, color: MU }}>{t('university.redeem.expires', 'Expires')}</span>
                <span style={{ fontSize: 13, fontWeight: 700, color: '#4ade80' }}>{t('university.redeem.never', 'Never! Yours forever')}</span>
              </div>
            )}
          </div>

          <motion.button whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} onClick={() => navigate('/university/enrol')}
            style={{ width: '100%', background: `linear-gradient(135deg, ${G}, #b8912e)`, color: '#06060c', border: 'none', borderRadius: 14, padding: '18px', fontSize: 16, fontWeight: 800, cursor: 'pointer', boxShadow: '0 6px 24px rgba(212,168,67,0.3)', marginBottom: 12 }}>
            {t('university.redeem.startBtn', 'Browse Degrees & Start Learning →')}
          </motion.button>
          <button onClick={() => navigate('/university/dashboard')} style={{ background: 'none', border: `1px solid ${BR}`, color: MU, borderRadius: 12, padding: '12px', fontSize: 14, cursor: 'pointer', width: '100%' }}>
            {t('university.redeem.dashboardBtn', 'Go to Dashboard')}
          </button>
        </motion.div>
      </div>
      <UniversityFooter />
    </>
    )
  }

  return (
    <>
    <UniversityHeader/>
    <div style={{ background: M, minHeight: '100vh', color: TX, fontFamily: "'Inter', system-ui, sans-serif", display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <SEO title="Claim Gift" noindex />
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        style={{ maxWidth: 480, width: '100%', margin: '0 auto', padding: '40px 24px' }}>

        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <div style={{ fontSize: 56, marginBottom: 16 }}>🎁</div>
          <h1 style={{ fontSize: 30, fontWeight: 900, margin: '0 0 10px', fontFamily: "'Playfair Display', Georgia, serif" }}>
            {t('university.redeem.title', 'Claim Your Gift')}
          </h1>
          <p style={{ color: MU, fontSize: 14, lineHeight: 1.65, margin: 0 }}>
            {t('university.redeem.subtitle', 'Someone gifted you an education at Still Waters University. Enter your code below to claim it.')}
          </p>
        </div>

        <div style={{ background: CA, border: `1px solid ${BR}`, borderRadius: 20, padding: '32px 28px' }}>
          <label style={{ fontSize: 12, fontWeight: 800, color: MU, letterSpacing: '1.5px', display: 'block', marginBottom: 10 }}>
            {t('university.redeem.codeLabel', 'GIFT CODE')}
          </label>
          <input
            value={code}
            onChange={e => setCode(e.target.value.toUpperCase())}
            placeholder="e.g. A1B2C3D4"
            maxLength={12}
            style={{
              width: '100%', padding: '16px 18px', borderRadius: 12,
              border: `2px solid ${code ? 'rgba(212,168,67,0.4)' : BR}`,
              background: 'rgba(255,255,255,0.04)', color: TX, fontSize: 20,
              fontFamily: "'Courier New', monospace", fontWeight: 700,
              letterSpacing: '4px', outline: 'none', boxSizing: 'border-box',
              textAlign: 'center', marginBottom: 20, textTransform: 'uppercase',
              transition: 'border-color 0.2s',
            }}
            onKeyDown={e => e.key === 'Enter' && handleRedeem()}
          />

          <AnimatePresence>
            {error && (
              <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                style={{ background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.3)', borderRadius: 10, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#f87171', fontWeight: 600 }}>
                {error}
              </motion.div>
            )}
          </AnimatePresence>

          {!user && !authLoading && (
            <div style={{ background: 'rgba(14,165,233,0.08)', border: '1px solid rgba(14,165,233,0.2)', borderRadius: 12, padding: '12px 16px', marginBottom: 16 }}>
              <p style={{ fontSize: 13, color: '#7dd3fc', margin: 0, lineHeight: 1.55 }}>
                {t('university.redeem.needAccount', "You'll need a free Still Waters account to claim your gift. We'll take you to sign up then bring you straight back.")}
              </p>
            </div>
          )}

          <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}
            onClick={() => handleRedeem()} disabled={loading || !code.trim()}
            style={{ width: '100%', background: code.trim() ? `linear-gradient(135deg, ${G}, #b8912e)` : 'rgba(255,255,255,0.06)', color: code.trim() ? '#06060c' : MU, border: 'none', borderRadius: 12, padding: '16px', fontSize: 15, fontWeight: 800, cursor: code.trim() ? 'pointer' : 'not-allowed', opacity: loading ? 0.7 : 1 }}>
            {loading ? t('university.redeem.claiming', 'Claiming…') : user ? t('university.redeem.claimBtn', 'Claim My Education →') : t('university.redeem.claimBtnGuest', 'Continue to Claim →')}
          </motion.button>
        </div>

        <p style={{ textAlign: 'center', fontSize: 13, color: MU, marginTop: 24 }}>
          {t('university.redeem.noCode', 'Want to give the gift of education instead?')}{' '}
          <button onClick={() => navigate('/university/gift')} style={{ background: 'none', border: 'none', color: G, cursor: 'pointer', fontSize: 13, fontWeight: 700, padding: 0 }}>
            {t('university.redeem.giftLink', 'Gift a degree →')}
          </button>
        </p>
      </motion.div>
    </div>
    <UniversityFooter />
    </>
  )
}
